import { useMemo } from 'react';

import { useVariableNames } from './hooks/useVariableNames';

/**
 * Trigger regex shared between the Lexical VariableTypeaheadPlugin and the
 * title editor's typeahead hook.
 *
 * Matches `{` preceded by start-of-string, whitespace, or `(`, followed by
 * any characters that are NOT `}`. When the user types `}`, the `[^}]*`
 * portion fails, the match returns null, and the menu closes automatically.
 */
export const VARIABLE_TRIGGER_REGEX = /(?:^|\s|\()(\{([^}]*))$/;

/**
 * Filter variable names by a case-insensitive substring match.
 * Returns all names when queryString is empty.
 */
function filterVariableNames(
  variableNames: string[],
  queryString: string
): string[] {
  const lower = queryString.toLowerCase();
  return variableNames.filter((name) => name.toLowerCase().includes(lower));
}

/**
 * Hook that returns filtered variable names based on a query string.
 * Returns an empty array when queryString is null (menu closed).
 */
export function useFilteredVariableNames(queryString: string | null): string[] {
  const variableNames = useVariableNames();

  return useMemo(() => {
    if (queryString === null) return [];
    return filterVariableNames(variableNames, queryString);
  }, [queryString, variableNames]);
}
